import useAuthenticatedUser from "@/hooks/useAuthenticatedUser";
import styled from "@emotion/styled";
import {
  extractCurrencyPart,
  formatInputAmount,
} from "@ternary/api-lib/analytics/utils/NumberFormatUtils";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import React, { useEffect, useRef, useState } from "react";
import TextInput, { Size } from "./TextInput";

// Create a styled version of TextInput with number input arrows hidden
const StyledTextInput = styled(TextInput)({
  /* For Chrome, Safari, Edge, Opera */
  "&[type='number']::-webkit-inner-spin-button, &[type='number']::-webkit-outer-spin-button":
    {
      "-webkit-appearance": "none",
      margin: 0,
    },
  /* For Firefox */
  "&[type='number']": {
    "-moz-appearance": "textfield",
  },
});

export interface Props {
  currency?: CurrencyCode;
  dataTestId?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  size?: Size;
  variant?: "danger" | "success";
  value: number;
  onChange: (number: number) => void;
}

export default function AmountInput({
  currency,
  dataTestId = "amount-input",
  disabled,
  min,
  max,
  size,
  variant,
  value: propValue,
  onChange,
}: Props): React.JSX.Element {
  const authenticatedUser = useAuthenticatedUser();

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(propValue.toString());
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const currencyCode =
    currency || authenticatedUser.tenant.preferredCurrencyCode;

  function handleOnFocus() {
    setIsFocused(true);
    setInputValue(propValue.toString());
    // Reset validation state when focusing
    setIsValid(true);
    setErrorMessage("");
  }

  function handleOnBlur() {
    setIsFocused(false);

    // Use HTML constraint validation API on blur
    let isInputValid = true;
    let htmlValidationMessage = "";

    if (inputRef.current) {
      isInputValid = inputRef.current.validity.valid;
      htmlValidationMessage = isInputValid
        ? ""
        : inputRef.current.validationMessage;
    }

    setIsValid(isInputValid);
    setErrorMessage(htmlValidationMessage);
    onChange(Number(inputValue));
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
  }

  useEffect(() => {
    if (!isFocused) {
      setInputValue(propValue.toString());
    }
  }, [propValue, isFocused]);

  function getDisplayValue() {
    if (isFocused) {
      return inputValue;
    } else {
      return formatInputAmount({
        currencyCode,
        number: Number(inputValue) || 0,
      });
    }
  }

  return (
    <StyledTextInput
      inputRef={inputRef}
      dataTestId={dataTestId}
      disabled={disabled}
      errorMessage={errorMessage}
      min={min}
      max={max}
      prefix={extractCurrencyPart("narrowSymbol", currencyCode)}
      size={size}
      suffix={currencyCode}
      variant={!isValid ? "danger" : variant}
      value={getDisplayValue()}
      type={isFocused ? "number" : "text"}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onChange={handleChange}
    />
  );
}
